import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DropdownImageUpload, EmojiButton } from '../../components';
import { DefaultProfilePicture, IMAGE_TYPE_ONLY_ALLOWED } from '../../rootAccess';
import './styles.scss';
import { useDispatch, useSelector } from 'react-redux';
import { handleChannelNavigation } from '../../utils';
import { Close2, DropdownArrow2 } from '../../../assets/images';
import logger from '../../../Helpers/logger';
const MAX_CHANNEL_NAME_LENGTH = 30;
const MAX_CHANNEL_DESC_LENGTH = 250;

function getCharLength(str) {
    return [...str].length;
}

const TextField = ({ label, name, value, onChange, onPaste, maxLength, required, inputRef, children, error }) => (
    <div className="form-group">
        <label htmlFor={name}>
            {label} {required && <span className="required">*</span>}
        </label>
        <div className="channel-create-input">
            <div className="channel_inputbox">
                <input
                    ref={inputRef}
                    type="text"
                    id={name}
                    name={name}
                    value={value}
                    onChange={onChange}
                    onPaste={onPaste}
                    placeholder={`Enter ${label.toLowerCase()}`}
                />
                {children}
            </div>
            {maxLength !== undefined && <div className="char-counter">{maxLength - [...value].length}</div>}
        </div>
        {error && <p className="errorMessage">{error}</p>}
    </div>
);

TextField.propTypes = {
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onPaste: PropTypes.func,
    maxLength: PropTypes.number,
    required: PropTypes.bool,
    inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
    children: PropTypes.node,
    error: PropTypes.string,
};

const TextAreaField = ({ label, name, value, onChange, onPaste, maxLength, children, error, required, inputRef }) => (
    <div className="form-group">
        <label htmlFor={name}>
            {label} {required && <span className="required">*</span>}
        </label>
        <div className="channel-create-input">
            <div className="channel_inputbox channel_textarea">
                <textarea
                    ref={inputRef}
                    id={name}
                    name={name}
                    value={value}
                    onChange={onChange}
                    onPaste={onPaste}
                    placeholder={`Describe your ${label.toLowerCase()}`}
                    className="no-resize"
                />
                <span className="char-counter">{maxLength - [...value].length}</span>
                {children}
            </div>
        </div>
        {error && <p className="errorMessage">{error}</p>}
    </div>
);

TextAreaField.propTypes = {
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onPaste: PropTypes.func,
    maxLength: PropTypes.number,
    error: PropTypes.string,
    required: PropTypes.bool,
};

export const ChannelInfoForm = ({
    data = {},
    onSubmit,
    onChange,
    showSubmit = false,
    submitLabel = 'Create Channel',
    showToggles = false,
    isEdit = false,
    onClose,
}) => {
    const initialDataRef = useRef({ ...data });
    const textInputRef = useRef(null);
    const descriptionInputRef = useRef(null);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        ...data,
        settings: { accessMode: 1, messageMode: 1, ...(data.settings || {}) }, // default values
    });
    const dispatch = useDispatch();
    const categories = useSelector(state => state.channelCategory.categories);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [selectedCategoryLabel, setSelectedCategoryLabel] = useState('');
    const submittingRef = useRef(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!initialDataRef.current.channelName) {
            initialDataRef.current = { ...data, categoryId: data.categoryId ? Number(data.categoryId) : '' };
            setFormData({
                ...data,
                categoryId: data.categoryId ? Number(data.categoryId) : '',
            });
        } else {
            setFormData({
                ...data,
                categoryId: data.categoryId ? Number(data.categoryId) : '',
            });
        }
    }, [data]);
    // close dropdown on outside click
    useEffect(() => {
        const onDocClick = e => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    // keep label in sync when categories or formData.categoryId changes
    useEffect(() => {
        const sel = categories?.find(c => Number(c.id) === Number(formData.categoryId));
        setSelectedCategoryLabel(sel ? sel.category : '');
    }, [categories, formData.categoryId]);

    // useEffect(() => {
    //    setCategoriesLoading(true);
    //    dispatch(fetchAndStoreChannelCategories()).finally(() => {
    //       setCategoriesLoading(false);
    //    });
    // }, [dispatch]);

    const updateForm = updatedFields => {
        const updated = { ...formData, ...updatedFields };
        setFormData(updated);
        if (typeof onChange === 'function') {
            onChange(updated);
        }
    };

    const updateSettings = updated => {
        setFormData(prev => ({
            ...prev,
            settings: {
                ...prev.settings,
                ...updated,
            },
        }));
    };

    const handleChange = e => {
        const { name, value } = e.target;
        const max = name === 'channelName' ? MAX_CHANNEL_NAME_LENGTH : MAX_CHANNEL_DESC_LENGTH;
        if (getCharLength(value) > max) return;
        updateForm({ [name]: value });
        setErrors({});
    };

    const handlePaste = (e, field) => {
        const pasted = e.clipboardData.getData('text');
        const input = e.target;
        const cursor = input.selectionStart;
        const current = formData[field];
        const newText = current.slice(0, cursor) + pasted + current.slice(cursor);

        const maxLength = field === 'channelName' ? MAX_CHANNEL_NAME_LENGTH : MAX_CHANNEL_DESC_LENGTH;

        if (getCharLength(newText) > maxLength) {
            e.preventDefault();
            const allowed = maxLength - getCharLength(current);
            const trimmedPaste = [...pasted].slice(0, allowed).join('');
            const updated = current.slice(0, cursor) + trimmedPaste + current.slice(cursor);
            updateForm({ [field]: updated });
        }
    };

    const insertEmoji = emoji => {
        const input = textInputRef.current;
        if (!input) return;
        const cursorPos = input.selectionStart;
        const currentText = formData.channelName;
        const before = currentText.slice(0, cursorPos);
        const after = currentText.slice(cursorPos);
        const newText = before + emoji + after;
        if (getCharLength(newText) > MAX_CHANNEL_NAME_LENGTH) return;
        updateForm({ channelName: newText });
        setTimeout(() => {
            input.focus();
            input.setSelectionRange(cursorPos + emoji.length, cursorPos + emoji.length);
        }, 0);
    };

    const insertEmojiInDescription = emoji => {
        const input = descriptionInputRef.current;

        if (!input) return;
        const cursorPos = input.selectionStart;
        const currentText = formData.description;
        const before = currentText.slice(0, cursorPos);
        const after = currentText.slice(cursorPos);
        const newText = before + emoji + after;
        if (getCharLength(newText) > MAX_CHANNEL_DESC_LENGTH) return;
        updateForm({ description: newText });
        setTimeout(() => {
            input.focus();
            input.setSelectionRange(cursorPos + emoji.length, cursorPos + emoji.length);
        }, 0);
    };

    const handleImageUpload = async fileOrBlob => {
        let file = fileOrBlob;
        if (!(file instanceof File)) {
            file = new File([fileOrBlob], 'profile-image.png', { type: 'image/png' });
        }
        if (!/\.(jpe?g|png|gif|bmp)$/i.test(file.name)) {
            alert(IMAGE_TYPE_ONLY_ALLOWED + ' jpeg, png, gif, bmp');
            return;
        }
        const urlCreator = window.URL || window.webkitURL;
        const imageUrl = urlCreator.createObjectURL(file);
        const imageBlob = await fetch(imageUrl).then(r => r.blob());
        updateForm({ imageUri: imageBlob });
    };

    const handleRemoveImage = () => {
        updateForm({ imageUri: '' });
    };

    const handleToggle = (field, _type) => {
        const current = formData.settings[field];

        if (field === 'accessMode') {
            updateSettings({ accessMode: current === 1 ? 2 : 1 });
        } else if (field === 'messageMode') {
            // Single toggle: allowMessage true -> messageMode = 1, false -> messageMode = 2
            const newVal = current === 1 ? 2 : 1;
            updateSettings({ messageMode: newVal });
        }
    };

    const TOGGLES = [
        {
            label: 'Channel Visibility',
            options: [
                { label: 'Public', value: 1 },
                { label: 'Private', value: 2 },
            ],
            field: 'accessMode',
            type: 'radio',
        },
        // {
        //    label: 'Announcement Channel',
        //    field: 'messageMode',
        //    type: 'checkbox',
        //    checkType: 'announcement',
        //    isChecked: () => formData.settings.messageMode === 2,
        //    isDisabled: () => formData.settings.messageMode === 1,
        // },
        {
            label: 'Allow user to Send Message',
            field: 'messageMode',
            type: 'checkbox',
            checkType: 'allowMessage',
            isChecked: () => formData.settings.messageMode === 1,
            isDisabled: () => false,
        },
    ];

    const isFormChanged = () => {
        const initial = initialDataRef.current;
        if (!initial) return false;

        const basicChanged =
            formData.channelName?.trim() !== initial.channelName ||
            formData.description?.trim() !== initial.description ||
            formData.imageUri !== initial.imageUri ||
            formData.categoryId !== initial.categoryId;

        const settingsChanged =
            formData.settings &&
            initial.settings &&
            (formData.settings.accessMode !== initial.settings.accessMode ||
                formData.settings.messageMode !== initial.settings.messageMode);

        return basicChanged || settingsChanged;
    };

    // form validity for create (mandatory fields)
    const isFormValid = () => {
        return !!formData.channelName && formData.channelName.trim().length > 0 && !!formData.categoryId;
    };

    const handleSubmit = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        if (!formData.channelName.trim()) {
            setErrors({ channelName: 'Channel name is required' });
            submittingRef.current = false;
            setSubmitting(false);
            return;
        }
        // if (!formData.description.trim()) {
        //    setErrors({ description: 'Description is required' });
        //    return;
        // }
        if (!formData.categoryId || isNaN(formData.categoryId)) {
            setErrors({ categoryId: 'Category is required' });
            submittingRef.current = false;
            setSubmitting(false);
            return;
        }
        setErrors({});
        const normalizedSettings = {
            ...(formData.settings || {}),
            messageMode: formData.settings && formData.settings.messageMode === 1 ? 1 : 2,
        };
        const payload = {
            ...formData,
            settings: normalizedSettings,
            category: Number(formData.categoryId),
        };
        delete payload.categoryId;
        try {
            // call onSubmit only once and await if it returns a promise
            const result = onSubmit ? onSubmit(payload) : null;
            // support promise-returning onSubmit
            if (result && typeof result.then === 'function') {
                await result;
            }

            // After successful submit, reset initialDataRef
            initialDataRef.current = { ...formData };

            // if editing, close the edit popup (parent onClose) when submit succeeds
            if (isEdit && typeof onClose === 'function') {
                try {
                    onClose();
                } catch (e) {
                    /* ignore */
                }
            }
        } catch (err) {
            logger.error('submit failed', err);
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };
    const renderRadioOptions = toggle =>
        toggle.options.map(opt => (
            <label key={opt.value} className="radio-label">
                <input
                    type="radio"
                    name={toggle.field}
                    value={opt.value}
                    checked={formData.settings[toggle.field] === opt.value}
                    onChange={() => updateSettings({ [toggle.field]: opt.value })}
                />
                <span className="custom-radio" />
                <span className="radio-text">{opt.label}</span>
            </label>
        ));

    const renderCheckbox = toggle => (
        <div className="toggle-switch">
            <input
                type="checkbox"
                checked={!!toggle.isChecked && toggle.isChecked()}
                disabled={toggle.isDisabled && toggle.isDisabled()}
                onChange={e => {
                    if (toggle.field === 'messageMode') {
                        // explicit mapping required: checked -> 1, unchecked -> 2
                        updateSettings({ messageMode: e.target.checked ? 1 : 2 });
                    } else {
                        handleToggle(toggle.field, toggle.checkType);
                    }
                }}
            />
            <span className="slider-channel" />
        </div>
    );

    const renderToggles = () =>
        TOGGLES.map(toggle => (
            <div className="form-group" key={toggle.label}>
                <label className="toggle-label">
                    {toggle.label}
                    {toggle.type === 'radio' && <div className="radio-group">{renderRadioOptions(toggle)}</div>}
                    {toggle.type === 'checkbox' && renderCheckbox(toggle)}
                </label>
            </div>
        ));
    const closePopup = () => {
        if (isEdit && typeof onClose === 'function') {
            try {
                onClose();
            } catch (e) {
                /* ignore */
            }
            return;
        }
        handleChannelNavigation({ pane: 'recentChannelList' });
    };

    return (
        <div className="create-channel-form">
            <div className="create_channelform">
                <div className="create-channel-close" onClick={closePopup}>
                    <i>
                        <Close2 />
                    </i>
                </div>
                <DropdownImageUpload
                    defaultImage={DefaultProfilePicture}
                    onCapture={handleImageUpload}
                    imageUri={formData.imageUri}
                    onRemove={handleRemoveImage}
                    isEdit={isEdit}
                />

                <TextField
                    label="Channel Name"
                    name="channelName"
                    value={formData.channelName}
                    onChange={handleChange}
                    onPaste={e => handlePaste(e, 'channelName')}
                    maxLength={MAX_CHANNEL_NAME_LENGTH}
                    inputRef={textInputRef}
                    required
                    error={errors.channelName}
                >
                    <EmojiButton insertEmoji={insertEmoji} targetRef={textInputRef} />
                </TextField>

                <TextAreaField
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    onPaste={e => handlePaste(e, 'description')}
                    inputRef={descriptionInputRef}
                    maxLength={MAX_CHANNEL_DESC_LENGTH}
                    // required
                    // error={errors.description}
                >
                    <EmojiButton insertEmoji={insertEmojiInDescription} targetRef={descriptionInputRef} />
                </TextAreaField>
                <div className="form-group">
                    <label>
                        {'Category'} {<span className="required">*</span>}
                    </label>
                    <div className="category-dropdown-wrapper">
                        <div
                            className={`select_box ${categoriesLoading ? 'loading' : ''} ${dropdownOpen ? 'open' : ''}`}
                            ref={dropdownRef}
                        >
                            <button
                                type="button"
                                aria-haspopup="listbox"
                                className="category-button"
                                aria-expanded={dropdownOpen}
                                onClick={() => !categoriesLoading && setDropdownOpen(v => !v)}
                            >
                                <h3>
                                    {categoriesLoading
                                        ? 'Loading categories...'
                                        : selectedCategoryLabel || 'Select a category'}
                                </h3>
                                <i>
                                    <DropdownArrow2 />
                                </i>
                            </button>

                            {!categoriesLoading && dropdownOpen && (
                                <ul
                                    role="listbox"
                                    tabIndex={-1}
                                    className="select_box_list"
                                    aria-label="Channel categories"
                                >
                                    {categories.map(cat => {
                                        const active = Number(cat.id) === Number(formData.categoryId);
                                        return (
                                            <li
                                                key={cat.id}
                                                role="option"
                                                aria-selected={active}
                                                className={active ? 'active' : ''}
                                                onClick={() => {
                                                    updateForm({ categoryId: Number(cat.id) });
                                                    setDropdownOpen(false);
                                                }}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        updateForm({ categoryId: Number(cat.id) });
                                                        setDropdownOpen(false);
                                                    }
                                                }}
                                                tabIndex={0}
                                            >
                                                {cat.category}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                        {categoriesLoading && (
                            <div className="dropdown-loader">
                                {/* Simple spinner or shimmer */}
                                <span className="spinner" />
                            </div>
                        )}
                        {errors.categoryId && <p className="errorMessage">{errors.categoryId}</p>}
                    </div>
                </div>
                {showToggles && renderToggles()}

                {showSubmit &&
                    (() => {
                        let buttonClass = 'create-channel-btn ';
                        let buttonDisabled = false;
                        let buttonText = submitLabel;

                        if (isEdit) {
                            if (isFormChanged()) {
                                buttonClass += 'primary';
                                buttonDisabled = false;
                            } else {
                                buttonClass += 'secondary';
                                buttonDisabled = true;
                            }
                            buttonText = 'Save Changes';
                        } else {
                            // For create flow: disable until mandatory fields are provided
                            const valid = isFormValid();
                            buttonClass += valid ? 'primary' : 'secondary';
                            buttonDisabled = !valid;
                        }

                        return (
                            <button
                                type="button"
                                className={buttonClass}
                                onClick={handleSubmit}
                                disabled={buttonDisabled || submitting}
                            >
                                {buttonText}
                            </button>
                        );
                    })()}
            </div>
        </div>
    );
};

// ✅ PropTypes
ChannelInfoForm.propTypes = {
    data: PropTypes.shape({
        channelName: PropTypes.string,
        description: PropTypes.string,
        imageUri: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        categoryId: PropTypes.number,
    }),
    onSubmit: PropTypes.func,
    onChange: PropTypes.func,
    showSubmit: PropTypes.bool,
    submitLabel: PropTypes.string,
    showToggles: PropTypes.bool,
    settings: PropTypes.shape({
        accessMode: PropTypes.number,
        messageMode: PropTypes.number,
    }),
    isEdit: PropTypes.bool,
    onClose: PropTypes.func,
};
